import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal, Popover2 } from '@evertrue/et-components';
import { FlexTable } from '@evertrue/sdk';
import EverTrue from 'app';
import Api from 'entities/helpers/api';
import useCadence from 'apps/cadences-v2/hooks/use-cadence';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { STEP_RECIPIENTS_QUERY } from 'apps/cadences-v2/utils/cadence-queries';
import { compileTemplate } from 'apps/outreach/utils/utils';
import FilterBar from 'apps/cadences-v2/components/filter-bar';

const CONTACT_DETAIL_QUERY = `query($id: BigInt!) {
  contact(id: $id) {
    id
    contact_attributes { name_full }
    emails { email primary }
  }
}`;

/** Fetch contact name and primary email from the contacts GraphQL API. */
const fetchContactDetails = (contactId) => {
  const numericId = Number(contactId);
  if (!numericId) return Promise.resolve(null);
  return Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({ query: CONTACT_DETAIL_QUERY, variables: { id: numericId } }),
  }).then((resp) => {
    const contact = resp?.data?.contact || resp?.contact;
    if (!contact) return null;
    const primaryEmail = (contact.emails || []).find((e) => e.primary)?.email || contact.emails?.[0]?.email || null;
    return {
      contactName: contact.contact_attributes?.name_full || null,
      contactEmail: primaryEmail,
    };
  });
};

/** Timeframe filter options — matches automated tasks list page. */
const TIMEFRAME_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: 'Last 7 days', value: 'last_7' },
  { label: 'Last 30 days', value: 'last_30' },
  { label: 'Last 90 days', value: 'last_90' },
  { label: 'This year', value: 'this_year' },
];

/** Convert timeframe filter value to sendAfter/sendBefore ISO strings for the backend. */
const timeframeToDateRange = (tf) => {
  if (!tf || tf.value === 'all') return {};
  const now = new Date();
  const msPerDay = 86400000;
  switch (tf.value) {
    case 'last_7':
      return { sendAfter: new Date(now.getTime() - 7 * msPerDay).toISOString() };
    case 'last_30':
      return { sendAfter: new Date(now.getTime() - 30 * msPerDay).toISOString() };
    case 'last_90':
      return { sendAfter: new Date(now.getTime() - 90 * msPerDay).toISOString() };
    case 'this_year':
      return { sendAfter: new Date(now.getFullYear(), 0, 1).toISOString() };
    default:
      return {};
  }
};

/** Delivery status → { label, cssModifier } */
const DELIVERY_STATUS_MAP = {
  delivered: { label: 'Delivered', modifier: 'is-delivered' },
  failed_send: { label: 'Failed Send', modifier: 'is-bounced' },
  skipped: { label: 'Skipped', modifier: 'is-skipped' },
  removed: { label: 'Removed', modifier: 'is-bounced' },
  pending: { label: 'Pending', modifier: 'is-pending' },
};

/** Format a date like "Dec 19, 2:00 PM" in the user's local timezone. */
const formatDateTime = (iso) => {
  if (!iso) return '\u2014';
  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return '\u2014';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: tz }) +
    ', ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz })
  );
};

/**
 * AutomatedTaskDetailController — drill-in detail view for a specific automated email step.
 * Uses the same FlexTable + FilterBar pattern as the automated tasks list page.
 * Route: /tasks/automated/:taskId where taskId = "cadenceId-eventId"
 */
const AutomatedTaskDetailController = ({ taskId }) => {
  const { cadenceId, eventId } = useMemo(() => {
    const parts = (taskId || '').split('-');
    if (parts.length >= 2) {
      return { cadenceId: parts[0], eventId: parts[1] };
    }
    return { cadenceId: taskId, eventId: null };
  }, [taskId]);

  const { cadence, loading: cadenceLoading } = useCadence(cadenceId);

  // Step recipients — server-side paginated
  const [stepRecipients, setStepRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [timeframe, setTimeframe] = useState(TIMEFRAME_OPTIONS[1]); // default: Last 7 days

  // Email preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [previewRecipientName, setPreviewRecipientName] = useState('');

  // Find specific event
  const event = useMemo(() => {
    if (!cadence?.events || !eventId) return null;
    return cadence.events.find((e) => String(e.id) === String(eventId));
  }, [cadence, eventId]);

  // Fetch step recipients with server-side date filter.
  // Fetches up to 500 rows — FlexTable handles client-side pagination of the fetched set.
  useEffect(() => {
    if (!cadenceId || !eventId) return;
    let cancelled = false;
    setRecipientsLoading(true);

    const dateRange = timeframeToDateRange(timeframe);
    cadenceGqlCall(STEP_RECIPIENTS_QUERY, {
      cadenceId: String(cadenceId),
      cadenceEventId: String(eventId),
      deliveryStatusFilter: ['DELIVERED'],
      pagination: { first: 500 },
      ...dateRange,
    })
      .then(async (data) => {
        if (cancelled) return;
        const edges = data?.stepRecipients?.edges || [];
        const recipients = edges.map((edge) => {
          const node = edge.node || {};
          return {
            id: node.cadenceMemberId || edge.cursor,
            contactId: node.contactId,
            contactName: node.name || (node.contactId ? `Contact ${node.contactId}` : 'Unknown'),
            contactEmail: node.email || null,
            sendDate: node.sendAt || null,
            deliveryStatus: node.deliveryStatus || null,
          };
        });

        // Enrich contacts missing name/email from Elasticsearch results
        const needsEnrichment = recipients.filter(
          (r) => r.contactId && (!r.contactEmail || !r.contactName || r.contactName.startsWith('Contact '))
        );
        if (needsEnrichment.length > 0) {
          const details = await Promise.all(
            needsEnrichment.map((r) => fetchContactDetails(r.contactId).catch(() => null))
          );
          const detailMap = {};
          needsEnrichment.forEach((r, i) => {
            if (details[i]) detailMap[String(r.contactId)] = details[i];
          });
          for (const r of recipients) {
            const d = detailMap[String(r.contactId)];
            if (d) {
              if (d.contactName) r.contactName = d.contactName;
              if (!r.contactEmail && d.contactEmail) r.contactEmail = d.contactEmail;
            }
          }
        }

        if (!cancelled) setStepRecipients(recipients);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setRecipientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cadenceId, eventId, timeframe]);

  // Client-side search (lightweight, runs on fetched set)
  const filteredRows = useMemo(() => {
    if (!searchQuery) return stepRecipients;
    const q = searchQuery.toLowerCase();
    return stepRecipients.filter(
      (r) => r.contactName?.toLowerCase().includes(q) || r.contactEmail?.toLowerCase().includes(q)
    );
  }, [stepRecipients, searchQuery]);

  /** Open email preview modal — compiles the template with the recipient's contact data. */
  const handlePreviewEmail = useCallback(
    async (recipient) => {
      const config = event?.eventConfig || {};
      const templateBody = config.html_body || config.text_body || '';
      const templateSubject = config.subject || '';

      if (!templateBody) {
        EverTrue.Alert.warn('No email template body found for this step.');
        return;
      }

      setPreviewRecipientName(recipient.contactName || 'Recipient');
      setPreviewLoading(true);
      setPreviewOpen(true);
      setPreviewSubject(templateSubject);
      setPreviewBody('');

      try {
        const [compiledBodyRes, compiledSubjectRes] = await Promise.all([
          compileTemplate(templateBody, recipient.contactId),
          templateSubject ? compileTemplate(templateSubject, recipient.contactId) : Promise.resolve(templateSubject),
        ]);
        const bodyResult =
          typeof compiledBodyRes === 'string'
            ? compiledBodyRes
            : compiledBodyRes?.body || compiledBodyRes?.data || compiledBodyRes;
        const subjectResult =
          typeof compiledSubjectRes === 'string'
            ? compiledSubjectRes
            : compiledSubjectRes?.body || compiledSubjectRes?.data || compiledSubjectRes;
        setPreviewBody(bodyResult || templateBody);
        setPreviewSubject(subjectResult || templateSubject);
      } catch {
        setPreviewBody(templateBody);
      } finally {
        setPreviewLoading(false);
      }
    },
    [event]
  );

  /** FilterBar definitions — single timeframe filter matching list page style. */
  const filterDefs = [
    {
      id: 'timeframe',
      label: 'Timeframe',
      type: 'select',
      options: TIMEFRAME_OPTIONS,
      value: timeframe,
      onChange: setTimeframe,
      alwaysVisible: true,
      alwaysShowLabel: true,
    },
  ];

  /** FlexTable column definitions. */
  const columns = useMemo(
    () => [
      {
        field: 'contactName',
        headerName: 'Constituent',
        flex: 1.2,
        minWidth: 180,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) =>
          row.contactId ? (
            <button
              type="button"
              className="automated-tasks--cadence-link"
              onClick={(e) => {
                e.stopPropagation();
                EverTrue.Navigator(`/contact/${row.contactId}`, true);
              }}
            >
              {row.contactName}
            </button>
          ) : (
            <span>{row.contactName}</span>
          ),
      },
      {
        field: 'contactEmail',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 200,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => <span style={{ color: '#6b7280' }}>{row.contactEmail || '\u2014'}</span>,
      },
      {
        field: 'sendDate',
        headerName: 'Send Date',
        flex: 0.8,
        minWidth: 150,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => <span>{formatDateTime(row.sendDate)}</span>,
      },
      {
        field: 'deliveryStatus',
        headerName: 'Delivery Status',
        flex: 0.7,
        minWidth: 130,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => {
          const info = DELIVERY_STATUS_MAP[(row.deliveryStatus || '').toLowerCase()] || {
            label: row.deliveryStatus || '\u2014',
            modifier: '',
          };
          return (
            <span className={`automated-tasks--cadence-type-badge automated-tasks--delivery-${info.modifier}`}>
              {info.label}
            </span>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        type: 'actions',
        renderCell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Popover2
              trigger={
                <Button type="simple" className="automated-tasks--action-btn">
                  <Icon icon="more-horiz" />
                </Button>
              }
            >
              {({ closePopover }) => (
                <div className="automated-tasks--action-menu">
                  {row.contactId && (
                    <button
                      type="button"
                      onClick={() => {
                        closePopover();
                        EverTrue.Navigator(`/contact/${row.contactId}`, true);
                      }}
                    >
                      View Contact
                    </button>
                  )}
                  {row.contactId && (event?.eventConfig?.html_body || event?.eventConfig?.text_body) && (
                    <button
                      type="button"
                      onClick={() => {
                        closePopover();
                        handlePreviewEmail(row);
                      }}
                    >
                      Preview Email
                    </button>
                  )}
                </div>
              )}
            </Popover2>
          </div>
        ),
      },
    ],
    [event, handlePreviewEmail]
  );

  if (cadenceLoading) {
    return <Loading />;
  }

  if (!cadence || !event) {
    return (
      <div className="automated-tasks-page">
        <div className="automated-tasks-page--header">
          <h1>Automated Task Detail</h1>
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          <p>This automated task could not be found. It may have been deleted or you may not have access.</p>
          <Button type="secondary" onClick={() => EverTrue.Navigator('/tasks/automated', true)}>
            Back to Automated Tasks
          </Button>
        </div>
      </div>
    );
  }

  const stepLabel = event.stepLabel || event.sequenceOrder || '';
  const eventType = event.eventType || 'email';

  return (
    <div className="automated-tasks-page">
      {/* Header — same pattern as list page */}
      <div className="automated-tasks-page--header">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <button
            type="button"
            className="automated-tasks--cadence-link"
            style={{ fontSize: 12, color: '#6b7280' }}
            onClick={() => EverTrue.Navigator('/tasks/automated', true)}
          >
            Automated Tasks
          </button>
          <span style={{ color: '#9ca3af', fontSize: 12 }}>&gt;</span>
          <button
            type="button"
            className="automated-tasks--cadence-link"
            style={{ fontSize: 12, color: '#6b7280' }}
            onClick={() => EverTrue.Navigator('/tasks/automated', true)}
          >
            Cadence Tasks
          </button>
          <span style={{ color: '#9ca3af', fontSize: 12 }}>&gt;</span>
          <span style={{ fontSize: 12, color: '#374151' }}>{cadence.name}</span>
        </div>
        <h1 style={{ marginTop: 4 }}>
          <button
            type="button"
            className="automated-tasks--cadence-link"
            onClick={() => {
              const path =
                cadence.cadenceType === 'bulk_message'
                  ? `/cadences-v2/bulk-message/${cadenceId}`
                  : `/cadences-v2/${cadenceId}`;
              EverTrue.Navigator(path, true);
            }}
          >
            {cadence.name}
          </button>
          <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>Constituents</span>
        </h1>
        <p className="automated-tasks-page--subtitle">
          Step {stepLabel}: {eventType.charAt(0).toUpperCase() + eventType.slice(1)}
          {event.eventConfig?.name ? ` — ${event.eventConfig.name}` : ''}
        </p>
      </div>

      {/* Filter bar — same component as list page, with timeframe + search */}
      <FilterBar
        filters={filterDefs}
        rightContent={
          <div className="automated-tasks-page--search-input">
            <Icon icon="search" />
            <input
              type="text"
              placeholder="Search constituents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        }
      />

      {/* Count */}
      <div style={{ padding: '8px 16px', color: '#6b7280', fontSize: 13 }}>
        {filteredRows.length.toLocaleString()} constituents
      </div>

      {/* FlexTable — same component as list page */}
      <div className="automated-tasks-page--table-container">
        <div style={{ height: 'calc(100vh - 320px)', width: '100%' }}>
          <FlexTable
            tableKey="automated-task-detail-table"
            columns={columns}
            rows={filteredRows}
            getRowId={(params) => params.id}
            loading={recipientsLoading}
            disableRowSelectionOnClick
            disableColumnMenu={true}
            showColumnVerticalBorder={false}
            sortingMode="client"
            sortingOrder={['asc', 'desc']}
            paginationMode="client"
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: { sortModel: [{ field: 'contactName', sort: 'asc' }] },
            }}
            getRowHeight={() => 'auto'}
            slots={{
              noRowsOverlay: () => (
                <div className="automated-tasks-page--empty">
                  <Icon icon="people" size={3} className="automated-tasks-page--empty-icon" />
                  <p>
                    No constituents found
                    {searchQuery ? ' matching your search.' : ' for this step in the selected timeframe.'}
                  </p>
                </div>
              ),
            }}
          />
        </div>
      </div>

      {/* Email Preview Modal */}
      <Modal isOpen={previewOpen} closeModal={() => setPreviewOpen(false)} style={{ width: 680, maxWidth: '90vw' }}>
        <div className="automated-step-detail--preview-modal">
          <div className="automated-step-detail--preview-header">
            <h2>Email Preview — {previewRecipientName}</h2>
            <button
              type="button"
              className="automated-step-detail--preview-close"
              onClick={() => setPreviewOpen(false)}
            >
              <Icon icon="close" />
            </button>
          </div>
          {previewLoading ? (
            <div className="automated-step-detail--preview-loading">
              <Loading />
              <p>Compiling template...</p>
            </div>
          ) : (
            <>
              <div className="automated-step-detail--preview-subject">
                <span className="automated-step-detail--preview-label">Subject</span>
                <span>{previewSubject || '\u2014'}</span>
              </div>
              <div className="automated-step-detail--preview-body">
                <div
                  dangerouslySetInnerHTML={{
                    __html: (previewBody || '')
                      .replace(/<script[\s\S]*?<\/script>/gi, '')
                      .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, ''),
                  }}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

AutomatedTaskDetailController.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AutomatedTaskDetailController;
